'use client';

// SortableSourceList - Drag-and-drop sortable list of content sources
// Supports hierarchical display with collapsible children

import { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useTranslations } from 'next-intl';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ContentSource } from '../../types/content-source';
import { ContentSourceItem } from './ContentSourceItem';

export interface SortableSourceListProps {
  /** List of content sources */
  sources: ContentSource[];
  /** Callback when order changes */
  onReorder: (orderedIds: string[]) => void;
  /** Callback when delete is clicked */
  onDelete: (id: string) => void;
  /** Callback when edit is clicked (only for text type) */
  onEdit?: ((id: string, content: string) => void) | undefined;
  /** Whether interactions are disabled */
  disabled?: boolean;
  /** Map of children by parent ID (for hierarchical display) */
  childrenByParent?: Map<string, ContentSource[]>;
  /** Set of expanded parent IDs */
  expandedParents?: Set<string>;
  /** Callback to toggle parent expansion */
  onToggleExpand?: (parentId: string) => void;
}

interface SortableItemProps {
  source: ContentSource;
  onDelete: (id: string) => void;
  onEdit?: ((id: string, content: string) => void) | undefined;
  disabled: boolean;
}

/**
 * Wrapper component that makes ContentSourceItem sortable
 */
function SortableItem({ source, onDelete, onEdit, disabled }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: source.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <ContentSourceItem
        source={source}
        onDelete={onDelete}
        onEdit={onEdit}
        isDragging={isDragging}
        disabled={disabled}
        dragHandleProps={{
          attributes: attributes as unknown as Record<string, unknown>,
          listeners: listeners as unknown as Record<string, unknown> | undefined,
        }}
      />
    </Box>
  );
}

/**
 * SortableSourceList - A list of content sources with drag-and-drop reordering
 *
 * Features:
 * - Drag-and-drop reordering using @dnd-kit
 * - Keyboard accessibility (Enter to pick up, arrow keys to move)
 * - Optimistic UI updates
 * - Drag overlay for visual feedback
 * - RTL support
 *
 * @example
 * ```tsx
 * <SortableSourceList
 *   sources={sources}
 *   onReorder={(ids) => reorderSources(ids)}
 *   onDelete={(id) => deleteSource(id)}
 * />
 * ```
 */
export function SortableSourceList({
  sources,
  onReorder,
  onDelete,
  onEdit,
  disabled = false,
  childrenByParent,
  expandedParents,
  onToggleExpand,
}: SortableSourceListProps) {
  const t = useTranslations('creator.contentSources');
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure sensors for pointer and keyboard interaction
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = sources.findIndex((s) => s.id === active.id);
        const newIndex = sources.findIndex((s) => s.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(sources, oldIndex, newIndex);
          onReorder(newOrder.map((s) => s.id));
        }
      }
    },
    [sources, onReorder]
  );

  // Find the active source for the drag overlay
  const activeSource = activeId
    ? sources.find((s) => s.id === activeId)
    : null;

  if (sources.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sources.map((s) => s.id)}
        strategy={verticalListSortingStrategy}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {sources.map((source) => {
            const children = childrenByParent?.get(source.id);
            const hasChildren = children && children.length > 0;
            const isExpanded = expandedParents?.has(source.id);

            return (
              <Box key={source.id}>
                {/* Parent item */}
                <SortableItem
                  source={source}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  disabled={disabled}
                />

                {/* Children toggle and list */}
                {hasChildren && (
                  <Box sx={{ ml: 2, mt: 0.5 }}>
                    {/* Toggle button */}
                    <Box
                      onClick={() => onToggleExpand?.(source.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <SubdirectoryArrowRightIcon
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {t('linkedPages', { count: children.length })}
                      </Typography>
                      <IconButton size="small" sx={{ p: 0.25 }}>
                        {isExpanded ? (
                          <ExpandLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Box>

                    {/* Collapsed children */}
                    <Collapse in={isExpanded ?? false}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          mt: 1,
                          pl: 2,
                          borderLeft: '2px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {children.map((child) => (
                          <ContentSourceItem
                            key={child.id}
                            source={child}
                            onDelete={onDelete}
                            onEdit={onEdit}
                            disabled={disabled}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </SortableContext>

      {/* Drag overlay - shows the item being dragged */}
      <DragOverlay>
        {activeSource ? (
          <ContentSourceItem
            source={activeSource}
            onDelete={() => {}}
            isDragging
            disabled
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default SortableSourceList;
